import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, XCircle, Download, Loader, ShieldCheck, RefreshCw } from 'lucide-react';
import { DeletionResult, useSessionContentAudit, ContentAuditStore } from '../../hooks/useProjects';

type ModalState = 'confirm' | 'running' | 'complete' | 'error' | 'verify';

interface Step {
  label: string;
  key: keyof DeletionResult | null;
  formatCount: (result: DeletionResult) => string;
}

const STEPS: Step[] = [
  {
    label: 'Session data cleared',
    key: 'tablesCleared',
    formatCount: (r) => `${r.tablesCleared.length} tables`
  },
  {
    label: 'Files deleted',
    key: 's3ObjectsDeleted',
    formatCount: (r) => `${r.s3ObjectsDeleted} objects`
  },
  {
    label: 'Device images deleted',
    key: 'deviceImagesS3Deleted',
    formatCount: (r) => `${r.deviceImagesS3Deleted} files`
  },
  {
    label: 'Graph nodes cleared',
    key: 'graphNodesDeleted',
    formatCount: (r) => `${r.graphNodesDeleted} nodes`
  },
  {
    label: 'AI vectors purged',
    key: 'vectorsDeleted',
    formatCount: (r) => r.vectorsDeleted > 0 ? `${r.vectorsDeleted} vectors` : 'confirmed'
  },
  {
    label: 'Audit log cleared',
    key: 'auditEventsDeleted',
    formatCount: (r) => `${r.auditEventsDeleted} events`
  }
];

interface DeletionProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  isProcessing: boolean;
  result?: DeletionResult | null;
  error?: string | null;
  sessionId?: string;
  auditOnly?: boolean;
}

interface UseDeletionModalStateArgs {
  isOpen: boolean;
  auditOnly: boolean;
  isProcessing: boolean;
  result?: DeletionResult | null;
  error?: string | null;
  sessionId?: string;
}

/**
 * Encapsulates the modal's state-transition effects: resetting on close,
 * kicking off audit-only mode, tracking processing/result/error, and
 * flipping into the verify view once an audit result arrives.
 */
function useDeletionModalState({
  isOpen,
  auditOnly,
  isProcessing,
  result,
  error,
  sessionId
}: UseDeletionModalStateArgs) {
  const [inputValue, setInputValue] = useState('');
  const [revealedSteps, setRevealedSteps] = useState(0);
  const [modalState, setModalState] = useState<ModalState>('confirm');
  const [verifyEnabled, setVerifyEnabled] = useState(false);

  const auditQuery = useSessionContentAudit(sessionId || '', !!sessionId && verifyEnabled);

  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setRevealedSteps(0);
      setModalState(auditOnly ? 'running' : 'confirm');
      setVerifyEnabled(false);
    }
  }, [isOpen, auditOnly]);

  // Audit-only mode: fire immediately on open
  useEffect(() => {
    if (isOpen && auditOnly) {
      setModalState('running');
      setVerifyEnabled(true);
    }
  }, [isOpen, auditOnly]);

  useEffect(() => {
    if (isProcessing) {
      setModalState('running');
      setRevealedSteps(0);
    }
  }, [isProcessing]);

  useEffect(() => {
    if (result && !isProcessing) {
      setModalState('complete');
      STEPS.forEach((_, i) => {
        setTimeout(() => setRevealedSteps(i + 1), i * 150);
      });
    }
  }, [result, isProcessing]);

  useEffect(() => {
    if (error && !isProcessing) {
      setModalState('error');
    }
  }, [error, isProcessing]);

  useEffect(() => {
    if (verifyEnabled && auditQuery.data) {
      setModalState('verify');
    }
  }, [verifyEnabled, auditQuery.data]);

  return {
    inputValue,
    setInputValue,
    revealedSteps,
    modalState,
    setModalState,
    verifyEnabled,
    setVerifyEnabled,
    auditQuery
  };
}

/** Mirrors the modal's original inline `canClose` boolean. */
function canCloseModal(modalState: ModalState, auditOnly: boolean): boolean {
  return (
    auditOnly ||
    modalState === 'confirm' ||
    modalState === 'complete' ||
    modalState === 'error' ||
    modalState === 'verify'
  );
}

export const DeletionProgressModal: React.FC<DeletionProgressModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  isProcessing,
  result,
  error,
  sessionId,
  auditOnly = false
}) => {
  const {
    inputValue,
    setInputValue,
    revealedSteps,
    modalState,
    setModalState,
    verifyEnabled,
    setVerifyEnabled,
    auditQuery
  } = useDeletionModalState({ isOpen, auditOnly, isProcessing, result, error, sessionId });

  if (!isOpen) return null;

  const isValid = inputValue.toLowerCase() === 'delete';
  const canClose = canCloseModal(modalState, auditOnly);

  const handleConfirm = () => {
    if (isValid && modalState === 'confirm') onConfirm();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleConfirm();
  };

  const handleClose = () => {
    if (canClose) onClose();
  };

  const handleVerify = () => {
    if (auditQuery.data) {
      setModalState('verify');
    } else {
      setVerifyEnabled(true);
    }
  };

  const handleReVerify = () => {
    auditQuery.refetch();
  };

  const downloadReceipt = () => {
    if (!result) return;
    const receipt = {
      deletedAt: new Date().toISOString(),
      title,
      sessionId,
      summary: {
        tablesCleared: result.tablesCleared.length,
        s3ObjectsDeleted: result.s3ObjectsDeleted,
        deviceImagesS3Deleted: result.deviceImagesS3Deleted,
        graphNodesDeleted: result.graphNodesDeleted,
        vectorsDeleted: result.vectorsDeleted,
        auditEventsDeleted: result.auditEventsDeleted
      },
      errors: result.errors,
      tablesCleared: result.tablesCleared,
      verification: auditQuery.data || null
    };
    const blob = new Blob([JSON.stringify(receipt, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deletion-receipt-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hasErrors = result && result.errors.length > 0;

  const getStoreStatus = (store: ContentAuditStore) => {
    if (store.count === -1) return 'unknown';
    if (store.key === 'auditEvents' && store.count <= 1) return 'clear';
    if (store.retained && store.count >= 0) return 'clear';
    if (store.count === 0) return 'clear';
    return 'residual';
  };

  const allClear = auditQuery.data
    ? auditQuery.data.stores.every(s => getStoreStatus(s) !== 'residual')
    : false;

  const renderModalHeader = () => {
    let icon: React.ReactNode;
    if (modalState === 'verify') {
      icon = <ShieldCheck size={24} color={allClear ? '#2f9e44' : '#fa5252'} />;
    } else if (modalState === 'complete' && !hasErrors) {
      icon = <CheckCircle size={24} color="#2f9e44" />;
    } else {
      icon = <AlertTriangle size={24} color="#fa5252" />;
    }

    let titleText: string;
    if (modalState === 'verify') {
      titleText = auditOnly ? 'Session data audit' : 'Deletion verified';
    } else if (modalState === 'complete') {
      titleText = 'Data deleted';
    } else {
      titleText = title;
    }
    return { icon, titleText };
  };

  const renderRunningState = () => (
    <div style={{ textAlign: 'center', padding: '12px 0' }}>
      <Loader size={32} color={auditOnly ? '#2f9e44' : '#fa5252'} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
      <p style={{ color: '#495057', fontSize: '15px', margin: 0 }}>
        {auditOnly ? 'Scanning data stores…' : 'Deleting all data…'}
      </p>
      <p style={{ color: '#868e96', fontSize: '13px', margin: '6px 0 0' }}>This may take a moment.</p>
    </div>
  );

  const renderCompleteState = () =>
    result ? (
      <div>
        {STEPS.map((step, i) => {
          const revealed = i < revealedSteps;
          return (
            <div key={step.label} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: i < STEPS.length - 1 ? '1px solid #f1f3f5' : 'none', opacity: revealed ? 1 : 0.25, transition: 'opacity 0.2s' }}>
              <CheckCircle size={18} color={revealed ? '#2f9e44' : '#ced4da'} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '14px', color: '#343a40' }}>{step.label}</span>
              {revealed && <span style={{ fontSize: '13px', color: '#868e96', fontVariantNumeric: 'tabular-nums' }}>{step.formatCount(result)}</span>}
            </div>
          );
        })}
        {hasErrors && (
          <div style={{ marginTop: '16px', background: '#fff5f5', border: '1px solid #ffc9c9', borderRadius: '6px', padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <XCircle size={16} color="#fa5252" />
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#c92a2a' }}>{result.errors.length} step{result.errors.length > 1 ? 's' : ''} had errors</span>
            </div>
            {result.errors.map((e) => <p key={e} style={{ fontSize: '12px', color: '#c92a2a', margin: '2px 0', fontFamily: 'monospace' }}>{e}</p>)}
          </div>
        )}
        {verifyEnabled && auditQuery.isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', color: '#868e96', fontSize: '13px' }}>
            <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
            Running live verification&hellip;
          </div>
        )}
      </div>
    ) : null;

  const renderVerifyState = () =>
    auditQuery.data ? (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <p style={{ margin: 0, fontSize: '13px', color: '#868e96' }}>Verified at {new Date(auditQuery.data.verifiedAt).toLocaleTimeString()}</p>
          <button onClick={handleReVerify} disabled={auditQuery.isFetching} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#495057', padding: '2px 4px' }}>
            <RefreshCw size={13} style={{ animation: auditQuery.isFetching ? 'spin 1s linear infinite' : 'none' }} />
            Re-check
          </button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e9ecef' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#495057', fontWeight: '600' }}>Store</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#495057', fontWeight: '600' }}>Records</th>
              {!auditOnly && <th style={{ textAlign: 'center', padding: '6px 8px', color: '#495057', fontWeight: '600' }}>Status</th>}
            </tr>
          </thead>
          <tbody>
            {[...auditQuery.data.stores].sort((a, b) => a.label.localeCompare(b.label)).map((store) => {
              const status = getStoreStatus(store);
              return (
                <tr key={store.key} style={{ borderBottom: '1px solid #f1f3f5' }}>
                  <td style={{ padding: '7px 8px', color: '#343a40' }}>{store.label}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#495057' }}>
                    {store.count === -1 ? '—' : store.count}
                    {store.note && <span style={{ marginLeft: '6px', color: '#adb5bd', fontSize: '11px' }}>{store.note}</span>}
                  </td>
                  {!auditOnly && (
                    <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                      {status === 'clear' && <CheckCircle size={16} color="#2f9e44" />}
                      {status === 'residual' && <XCircle size={16} color="#fa5252" />}
                      {status === 'unknown' && <span style={{ color: '#adb5bd', fontSize: '11px' }}>N/A</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {allClear ? (
          <div style={{ marginTop: '14px', background: '#ebfbee', border: '1px solid #8ce99a', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#2f9e44', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldCheck size={16} />All stores verified clean. No residual customer data found.
          </div>
        ) : !auditOnly && (
          <div style={{ marginTop: '14px', background: '#fff5f5', border: '1px solid #ffc9c9', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#c92a2a', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={16} />Residual data found. Contact support if deletion errors occurred.
          </div>
        )}
      </div>
    ) : null;

  const { icon: headerIcon, titleText } = renderModalHeader();

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'white', borderRadius: '8px', maxWidth: '560px', width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)', overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {headerIcon}
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{titleText}</h2>
          </div>
          <button onClick={handleClose} disabled={!canClose} style={{ background: 'none', border: 'none', cursor: canClose ? 'pointer' : 'not-allowed', padding: '4px', display: 'flex', alignItems: 'center', opacity: canClose ? 1 : 0.4 }}>
            <X size={20} color="#868e96" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {modalState === 'confirm' && (
            <>
              <p style={{ margin: '0 0 16px', color: '#495057', fontSize: '15px', lineHeight: '1.6' }}>{message}</p>
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '10px 14px', marginBottom: '20px', fontSize: '13px', color: '#856404' }}>
                Archiving the project does not remove this data. Use this action to permanently delete all customer data.
              </div>
              <p style={{ margin: '0 0 8px', color: '#495057', fontSize: '14px' }}>Type <strong>delete</strong> to confirm:</p>
              <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={handleKeyDown} autoFocus placeholder="delete" style={{ width: '100%', padding: '8px 12px', border: `1px solid ${isValid ? '#fa5252' : '#ced4da'}`, borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }} />
            </>
          )}
          {modalState === 'running' && renderRunningState()}
          {(modalState === 'complete' || modalState === 'error') && renderCompleteState()}
          {modalState === 'error' && !result && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <XCircle size={32} color="#fa5252" style={{ marginBottom: '12px' }} />
              <p style={{ color: '#c92a2a', fontSize: '15px', margin: 0 }}>{error || 'Deletion failed'}</p>
            </div>
          )}
          {modalState === 'verify' && renderVerifyState()}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e9ecef', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#f8f9fa' }}>
          {modalState === 'running' && auditOnly && (
            <button onClick={handleClose} style={{ padding: '8px 16px', background: 'white', color: '#495057', border: '1px solid #ced4da', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Cancel</button>
          )}
          {!auditOnly && modalState === 'confirm' && (
            <>
              <button onClick={handleClose} style={{ padding: '8px 16px', background: 'white', color: '#495057', border: '1px solid #ced4da', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Cancel</button>
              <button onClick={handleConfirm} disabled={!isValid} style={{ padding: '8px 16px', background: isValid ? '#fa5252' : '#adb5bd', color: 'white', border: 'none', borderRadius: '6px', cursor: isValid ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '500' }}>Delete all data</button>
            </>
          )}
          {!auditOnly && (modalState === 'complete' || modalState === 'error') && (
            <>
              {result && <button onClick={downloadReceipt} style={{ padding: '8px 16px', background: 'white', color: '#495057', border: '1px solid #ced4da', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}><Download size={14} />Download receipt</button>}
              {sessionId && <button onClick={handleVerify} disabled={verifyEnabled && auditQuery.isLoading} style={{ padding: '8px 16px', background: 'white', color: '#2f9e44', border: '1px solid #8ce99a', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}><ShieldCheck size={14} />Verify deletion</button>}
              <button onClick={handleClose} style={{ padding: '8px 16px', background: '#343a40', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Done</button>
            </>
          )}
          {modalState === 'verify' && (
            <>
              <button onClick={downloadReceipt} style={{ padding: '8px 16px', background: 'white', color: '#495057', border: '1px solid #ced4da', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}><Download size={14} />{auditOnly ? 'Download report' : 'Download receipt'}</button>
              <button onClick={handleClose} style={{ padding: '8px 16px', background: '#343a40', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Close</button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};
